import { useCallback, useEffect, useRef, useState } from 'react'
import { useRecorder } from '@/hooks/useRecorder'
import { useToast } from '@/components/providers/ToastProvider'
import { useOnlineStatus } from '@/hooks/useOnlineStatus'
import { getPendingAutoReloadUntil } from '@/lib/autoReload'
import { triggerImmediateReload } from '@/lib/appReload'
import { useStore } from '@/stores'

function canUseServiceWorker(): boolean {
  return typeof window !== 'undefined' && 'serviceWorker' in navigator
}

async function getRegistrationSafe(): Promise<ServiceWorkerRegistration | null> {
  try {
    return (await navigator.serviceWorker.getRegistration()) ?? null
  } catch {
    return null
  }
}

export function UpdateAvailableBanner(): JSX.Element | null {
  const [updateAvailable, setUpdateAvailable] = useState(false)
  const [isApplying, setIsApplying] = useState(false)
  const [secondsRemaining, setSecondsRemaining] = useState<number | null>(null)
  const registrationRef = useRef<ServiceWorkerRegistration | null>(null)
  const isRecording = useStore((state) => state.isRecording)
  const pendingAutoReloadUntil =
    typeof window !== 'undefined' ? getPendingAutoReloadUntil() : null
  const hasPendingAutoReload =
    typeof pendingAutoReloadUntil === 'number' && pendingAutoReloadUntil > Date.now()

  useEffect(() => {
    if (!canUseServiceWorker()) return

    let cancelled = false

    const attachRegistrationListeners = (reg: ServiceWorkerRegistration): void => {
      reg.addEventListener('updatefound', () => {
        const installing = reg.installing
        if (!installing) return
        installing.addEventListener('statechange', () => {
          // When installed with an existing controller, it means an update is waiting.
          if (installing.state === 'installed' && navigator.serviceWorker.controller) {
            registrationRef.current = reg
            if (!cancelled) setUpdateAvailable(true)
          }
        })
      })
    }

    const init = async (): Promise<void> => {
      const reg = await getRegistrationSafe()
      if (cancelled || !reg) return
      registrationRef.current = reg
      if (reg.waiting) {
        setUpdateAvailable(true)
      }
      attachRegistrationListeners(reg)
    }

    void init()

    const refreshRegistration = async (): Promise<void> => {
      const reg = registrationRef.current ?? (await getRegistrationSafe())
      if (!reg) return
      registrationRef.current = reg
      try {
        await reg.update()
      } catch {
        // ignore update failures (offline, Safari quirks)
      }
      if (reg.waiting) {
        setUpdateAvailable(true)
      }
    }

    const handleReturnToApp = (): void => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return
      void refreshRegistration()
    }

    window.addEventListener('focus', handleReturnToApp)
    window.addEventListener('pageshow', handleReturnToApp)
    document.addEventListener('visibilitychange', handleReturnToApp)

    return () => {
      cancelled = true
      window.removeEventListener('focus', handleReturnToApp)
      window.removeEventListener('pageshow', handleReturnToApp)
      document.removeEventListener('visibilitychange', handleReturnToApp)
    }
  }, [])

  const applyUpdate = useCallback(async (): Promise<void> => {
    if (isApplying) return
    setIsApplying(true)

    if (!canUseServiceWorker()) {
      triggerImmediateReload()
      return
    }

    const reg = registrationRef.current ?? (await getRegistrationSafe())
    registrationRef.current = reg

    // If we have a waiting worker, ask it to activate then reload when it takes control.
    if (reg?.waiting) {
      reg.waiting.postMessage({ type: 'SKIP_WAITING' })

      // Workbox generateSW doesn't `clientsClaim()` by default, so `controllerchange` may not fire
      // until after a navigation. We reload shortly after skipWaiting to pick up the new SW + assets.
      window.setTimeout(() => triggerImmediateReload(), 750)
      return
    }

    // No waiting worker; a normal reload is fine.
    triggerImmediateReload()
  }, [isApplying])

  useEffect(() => {
    if (!updateAvailable) return
    if (isApplying) return
    if (hasPendingAutoReload) return
    if (isRecording) {
      // Defer SW update while recording — the reload would kill an in-flight capture.
      setSecondsRemaining(null)
      return
    }

    setSecondsRemaining(5)

    const intervalId = window.setInterval(() => {
      setSecondsRemaining((prev) => {
        if (typeof prev !== 'number') return prev
        return Math.max(0, prev - 1)
      })
    }, 1000)

    const timeoutId = window.setTimeout(() => {
      void applyUpdate()
    }, 5000)

    return () => {
      window.clearInterval(intervalId)
      window.clearTimeout(timeoutId)
    }
  }, [applyUpdate, updateAvailable, isApplying, hasPendingAutoReload, isRecording])

  useEffect(() => {
    if (!updateAvailable) return
    if (!hasPendingAutoReload) return

    const activateWaitingWorker = async (): Promise<void> => {
      if (!canUseServiceWorker()) return
      const reg = registrationRef.current ?? (await getRegistrationSafe())
      registrationRef.current = reg
      if (reg?.waiting) {
        reg.waiting.postMessage({ type: 'SKIP_WAITING' })
      }
    }

    void activateWaitingWorker()
  }, [updateAvailable, hasPendingAutoReload])

  if (!updateAvailable) return null
  if (hasPendingAutoReload) return null

  return (
    <div
      className="bg-orange/95 text-white px-4 py-2 text-sm text-center border-b border-orange-dark/40 flex items-center justify-center gap-2 flex-wrap"
      role="status"
      aria-live="polite"
    >
      <span className="font-medium">Update available.</span>{' '}
      {isApplying ? (
        <span>Updating…</span>
      ) : isRecording ? (
        <>
          <span>Will install after you stop recording.</span>
          <button
            type="button"
            onClick={() => void applyUpdate()}
            className="underline hover:no-underline focus:outline-none focus:ring-2 focus:ring-white rounded px-2"
          >
            Install now
          </button>
        </>
      ) : (
        <span>
          Refreshing in {typeof secondsRemaining === 'number' ? `${secondsRemaining}s` : '5s'} to
          install the latest version.
        </span>
      )}
    </div>
  )
}

export function OfflineBanner() {
  const isOnline = useOnlineStatus()
  const [wasOffline, setWasOffline] = useState(false)
  const { showToast } = useToast()

  useEffect(() => {
    // Only show toasts on transitions; avoid toast spam on initial mount.
    if (!isOnline) {
      if (!wasOffline) {
        setWasOffline(true)
        showToast('Working offline. All data saved locally.', 'info', 4000)
      }
      return
    }

    if (wasOffline) {
      showToast('Connection restored', 'success', 3000)
      setWasOffline(false)
    }
  }, [isOnline, showToast, wasOffline])

  if (isOnline) return null

  return (
    <div
      className="bg-blue-600/90 text-white px-4 py-2 text-sm text-center border-b border-blue-700/50"
      role="status"
      aria-live="polite"
    >
      <span className="font-medium">Offline Mode:</span> App works offline. All recordings and data
      are saved locally.
    </div>
  )
}

export function MicPermissionBanner() {
  const { hasPermission, error, checkPermission } = useRecorder()
  const { showToast } = useToast()

  useEffect(() => {
    if (hasPermission === false) {
      showToast(
        'Microphone access is required for recording. Please enable it in your browser settings.',
        'error',
        8000
      )
    }
  }, [hasPermission, showToast])

  if (hasPermission !== false) return null

  return (
    <div
      className="bg-red-600 text-white px-4 py-2 text-sm text-center"
      role="alert"
      aria-live="polite"
    >
      <div className="flex items-center justify-center gap-2">
        <span className="font-medium">Microphone Access Required</span>
        <button
          onClick={checkPermission}
          className="underline hover:no-underline focus:outline-none focus:ring-2 focus:ring-white rounded px-2"
          aria-label="Retry microphone permission"
        >
          Retry
        </button>
      </div>
      {error && <p className="text-xs mt-1 opacity-90">{error}</p>}
    </div>
  )
}
